import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  getTranslatedLanguageCode,
  handleGoogleTranslatorOnPageLoad,
  handleTranslateClick
} from "../Chat/GoogleTranslateElement";
import { getHomeLandingPageBaseUrl, getLogoImage } from "../../ApplicationSettings";

import "./FindDentistHeader.css";
import { useLocation } from "react-router-dom";
import { ChevronDown } from "lucide-react";
import { WakeUpDentistMatchServer } from "../../helpers/requests";

const LANGUAGES = [
  { code: "en", label: "English" },
  { code: "es", label: "Español" },
  { code: "zh-CN", label: "中文(简体)" },
  { code: "zh-TW", label: "中文(繁體)" },
  { code: "tl", label: "Filipino" },
  { code: "vi", label: "Tiếng Việt" },
  { code: "ar", label: "العربية" },
  { code: "fr", label: "Français" },
  { code: "ko", label: "한국어" },
  { code: "pt", label: "Português" },
  { code: "ru", label: "Русский" }
];

const navLinks = [
  {
    href: "/dentist-enrollment",
    text: "Are you a Dentist? Get Listed!",
    className: "btn btn-secondary mx-md-1"
  },
  {
    href: "/sign-up",
    text: "Immediate Online Emergency Visit",
    className: "btn btn-danger mx-md-1"
  },
  {
    href: "/sign-up",
    text: "Talk to a Dentist Now — 24/7",
    className: "btn btn-primary fw-semibold mx-md-1"
  }
];

const NavLink = React.memo(({ href, className, children }) => (
  <a href={href} className={className}>
    {children}
  </a>
));

const clearGoogTransCookies = () => {
  const hostname = window.location.hostname;
  const expiry = "expires=Thu, 01 Jan 1970 00:00:00 UTC";
  const paths = ["/", ""];

  const hostParts = hostname.split(".");
  const domains = ["", hostname, `.${hostname}`];
  if (hostParts.length > 2) {
    const rootDomain = hostParts.slice(-2).join(".");
    domains.push(rootDomain, `.${rootDomain}`);
  }

  domains.forEach((domain) => {
    paths.forEach((path) => {
      const domainPart = domain ? `; domain=${domain}` : "";
      const pathPart = path ? `; path=${path}` : "";
      document.cookie = `googtrans=; ${domainPart}${pathPart}; ${expiry}`;
      document.cookie = `googtrans=/en/en; ${domainPart}${pathPart}; ${expiry}`;
    });
  });
};

const switchLanguage = (langCode) => {
  clearGoogTransCookies();

  const val = langCode === "en" ? "/en/en" : `/en/${langCode}`;
  const hostname = window.location.hostname;
  document.cookie = `googtrans=${val}; path=/`;
  document.cookie = `googtrans=${val}; domain=${hostname}; path=/`;
  document.cookie = `googtrans=${val}; domain=.${hostname}; path=/`;

  const combo = document.querySelector(".goog-te-combo");
  if (combo) {
    combo.value = langCode === "en" ? "" : langCode;
    combo.dispatchEvent(new Event("change"));

    const frame = document.querySelector(".goog-te-banner-frame");
    if (frame) frame.remove();
    document.documentElement.classList.remove("translated-ltr", "translated-rtl");
    document.body.style.removeProperty("top");
    return;
  }

  window.location.reload();
};

const FindDentistHeader = () => {
  const [currentLangCode, setCurrentLangCode] = useState("en");
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const logo = getLogoImage();

  const langRef = useRef(null);
  const location = useLocation();

  useEffect(() => {
    const code = getTranslatedLanguageCode() || "en";
    setCurrentLangCode(code);
  }, []);

  useEffect(() => {
    WakeUpDentistMatchServer();
    const onClickOutside = (e) => {
      if (langRef.current && !langRef.current.contains(e.target)) {
        setLangOpen(false);
      }
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  useEffect(() => {
    return () => {
      const DENTIST_MATCH_PATHS = [
        "/find-dentist",
        "/dentists",
        "/questionnaires",
        "/dentist-enrollment"
      ];

      setTimeout(() => {
        const path = window.location.pathname;
        const stillInDentistMatch = DENTIST_MATCH_PATHS.some((p) =>
          path.startsWith(p)
        );
        if (stillInDentistMatch) return;

        clearGoogTransCookies();
        const hostname = window.location.hostname;
        document.cookie = `googtrans=/en/en; path=/`;
        document.cookie = `googtrans=/en/en; domain=${hostname}; path=/`;
        document.cookie = `googtrans=/en/en; domain=.${hostname}; path=/`;

        const combo = document.querySelector(".goog-te-combo");
        if (combo) {
          combo.value = "en";
          combo.dispatchEvent(new Event("change"));
        }

        const frame = document.querySelector(".goog-te-banner-frame");
        if (frame) frame.remove();

        document.documentElement.classList.remove(
          "translated-ltr",
          "translated-rtl"
        );
        document.body.style.removeProperty("top");
      }, 0);
    };
  }, []);

  useEffect(() => {
    if (!window.googleTranslateElementInit) {
      window.googleTranslateElementInit = () => {
        new window.google.translate.TranslateElement(
          {
            pageLanguage: "en",
            includedLanguages: "en,es,zh-CN,zh-TW,tl,vi,ar,fr,ko,pt,ru",
            autoDisplay: false
          },
          "google_translate_element"
        );
      };

      const script = document.createElement("script");
      script.src =
        "//translate.google.com/translate_a/element.js?cb=googleTranslateElementInit";
      script.async = true;

      document.body.appendChild(script);
    }
  }, []);

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);

  const activeLang =
    LANGUAGES.find((l) => l.code === currentLangCode) || LANGUAGES[0];

  const handleSelect = useCallback(
    (code) => {
      setLangOpen(false);

      if (code === currentLangCode) return;

      setCurrentLangCode(code);
      switchLanguage(code);
    },
    [currentLangCode]
  );

  return (
    <header className="border-bottom" style={{ background: "#fff" }}>
      <div className="d-flex flex-wrap align-items-center justify-content-between p-3">
        <a
          href={getHomeLandingPageBaseUrl()}
          className="d-flex align-items-center text-decoration-none flex-shrink-0"
        >
          {logo && (
            <img
              src={logo}
              alt="Logo"
              className="img-fluid"
              style={{ height: "45px" }}
            />
          )}
        </a>

        <button
          className="btn border-0 shadow-none p-1 fd-header-hamburger"
          onClick={toggleMenu}
          aria-label="Toggle navigation menu"
          style={{ background: "transparent" }}
        >
          <svg
            width="30"
            height="30"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <line x1="3" y1="12" x2="21" y2="12"></line>
            <line x1="3" y1="6" x2="21" y2="6"></line>
            <line x1="3" y1="18" x2="21" y2="18"></line>
          </svg>
        </button>

        <div className={`fd-header-nav ${isMenuOpen ? "fd-header-nav-open" : ""}`}>
          <nav className="d-flex w-100"
            style={{ gap: "4px" }}
          >
            {navLinks.map(({ href, text, className }) => (
              <React.Fragment key={`${href}-${text}`}>
                <NavLink href={href} className={className}>
                  {text}
                </NavLink>
              </React.Fragment>
            ))}


            {/* 
             TODO: Update the language button in the navbar soon. For now, use the language change method used on Dental.com pages.
             
            <div id="google_translate_element" className="d-none"></div> 
            <div
              ref={langRef}
              className="position-relative skiptranslate notranslate"
              translate="no"
              style={{ marginLeft: "4px" }}
            >
              <button
                className="btn btn-secondary d-flex align-items-center justify-content-center gap-2 w-100 notranslate"
                onClick={() => setLangOpen(!langOpen)}
                type="button"
                translate="no"
                style={{ minHeight: "34px", padding: "6px 16px" }}
              >
                <span translate="no">🌐 {activeLang.label}</span>
                <ChevronDown
                  size={14}
                  style={{
                    transition: "transform .2s",
                    transform: langOpen ? "rotate(180deg)" : "none"
                  }}
                />
              </button>

              {langOpen && (
                <ul
                  className="position-absolute end-0 mt-1 list-unstyled bg-white border rounded shadow-lg py-1 notranslate"
                  translate="no"
                  style={{
                    zIndex: 1050,
                    minWidth: "160px",
                    maxHeight: "300px",
                    overflowY: "auto"
                  }}
                >
                  {LANGUAGES.map((lang) => (
                    <li key={lang.code}>
                      <button
                        className={`dropdown-item px-3 py-2 border-0 bg-transparent w-100 text-start notranslate${
                          lang.code === currentLangCode
                            ? " fw-bold text-primary"
                            : " text-dark"
                        }`}
                        onClick={() => handleSelect(lang.code)}
                        translate="no"
                        style={{ cursor: "pointer" }}
                      >
                        {lang.label}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div> */}
          </nav>
        </div>
      </div>
    </header>
  );
};

export default FindDentistHeader;
